import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import AuthVerify from './pages/AuthVerify';
import Dashboard from './pages/Dashboard';
import Queue from './pages/Queue';
import Review from './pages/Review';
import Delivered from './pages/Delivered';
import Audit from './pages/Audit';
import Compositions from './pages/Compositions';
import CompositionDetail from './pages/CompositionDetail';
import Settings from './pages/Settings';
import Demo from './pages/Demo';
import Architecture from './pages/Architecture';
import Layout from './components/Layout';
import { loadSession, tryAdoptPortalSession } from './api';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = loadSession();
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  // Portal bridge (Task 038.7): if we land here without a localStorage
  // session BUT the user has already authenticated at
  // cadenceadvisers.com/portal/login (cookie scoped to .cadenceadvisers.com),
  // adopt that session before the route guards fire. Prevents a forced
  // re-login when navigating from /portal/* into the Tempo admin SPA.
  const [bootstrapping, setBootstrapping] = useState(!loadSession());

  useEffect(() => {
    if (!bootstrapping) return;
    tryAdoptPortalSession().finally(() => setBootstrapping(false));
  }, [bootstrapping]);

  if (bootstrapping) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'monospace', fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b7280' }}>
        Signing you in…
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/verify" element={<AuthVerify />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="queue" element={<Queue />} />
        <Route path="review/:sessionId" element={<Review />} />
        <Route path="delivered" element={<Delivered />} />
        <Route path="audit" element={<Audit />} />
        <Route path="compositions" element={<Compositions />} />
        <Route path="compositions/:correlationId" element={<CompositionDetail />} />
        <Route path="settings" element={<Settings />} />
        <Route path="demo" element={<Demo />} />
        <Route path="architecture" element={<Architecture />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
